<template>
  <div class="slide-aside-container">
    <!-- MAIN APP -->
    <div class="slide-aside-main" :style="mainTransform">
      <slot />
    </div>

    <!-- LOG PANEL with Vuetify transition -->
    <transition name="aside-slide" appear>
      <aside
        v-if="modelValue"
        class="slide-aside-panel"
        :style="{ width: rightWidth }"
      >
        <slot name="right" />
      </aside>
    </transition>
    
    <!-- Expand toggle button (outside the panel) -->
    <!-- <v-btn
      v-if="modelValue"
      icon
      size="small"
      variant="plain"
      color="primary"
      class="expand-toggle-btn"
      :style="toggleButtonStyle"
      @click="toggleExpand"
    >
      <v-icon>{{ expanded ? 'mdi-arrow-collapse-right' : 'mdi-arrow-expand-left' }}</v-icon>
    </v-btn> -->
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { useGoTo } from 'vuetify'

@Options({
  name: 'SlideAside',
  props: {
    modelValue: Boolean,
    width: { type: Number, default: 320 },
    maxWidth: { type: Number, default: 50 }
  },
  emits: ['update:modelValue']
})
export default class SlideAside extends Vue {
  readonly modelValue!: boolean
  readonly width!: number
  readonly maxWidth!: number
  expanded = true
  goTo = useGoTo()

  get rightWidth() { return this.expanded ? `${this.maxWidth}%` : `${this.width}px` }

  get mainTransform() {
    /* identical timing to Vuetify transition (300 ms) */
    return {
      transform: this.modelValue
        ? `translateX(-${this.rightWidth})`
        : 'translateX(0)',
      transition: 'transform .3s cubic-bezier(.25,.8,.5,1)',
      willChange: 'transform',
      filter: this.modelValue ? 'blur(2px)' : 'none',
    }
  }

  get toggleButtonStyle() {
    // Position at the left edge of the panel
    return {
      position: 'absolute',
      bottom: '10px',
      right: this.rightWidth, // Position at the left edge of the panel
      zIndex: 10,
      opacity: 0.7,
      transition: 'right .3s cubic-bezier(.25,.8,.5,1)', // Match panel transition
      backgroundColor: 'rgba(13, 17, 23, 0.7)', // Semi-transparent background matching panel
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
      'margin-right': '12px'
    }
  }

  async updated() {
    // await this.goTo(0, { container: '#bottom' })
  }

  /* helpers (optional) */
  open()  { this.$emit('update:modelValue', true) }
  close() { this.$emit('update:modelValue', false) }
  toggle(){ this.$emit('update:modelValue', !this.modelValue) }
  toggleExpand() {this.expanded = !this.expanded }
}
</script>

<style lang="scss" scoped>
/* ───────── container & main app ───────── */
.slide-aside-container {
  position: relative;
  overflow: hidden;
  height: 100%;
}
.slide-aside-main {
  height: 100%;
}

/* expand toggle button hover effect */
.expand-toggle-btn:hover {
  opacity: 1 !important;
}

/* ───── custom “aside-slide” transition ───── */
.aside-slide-enter-from,
.aside-slide-leave-to {
  transform: translateX(100%);   // start/leave fully off-screen right
}

.aside-slide-enter-active,
.aside-slide-leave-active {
  transition: transform .3s cubic-bezier(.25,.8,.5,1);
}

.aside-slide-enter-to,
.aside-slide-leave-from {
  transform: translateX(0);      // end at final position
}

/* ───────── terminal-style side panel ──── */
.slide-aside-panel {
  position: absolute;
  top: 0; right: 0; bottom: 0;

  background-color: rgb(var(--v-theme-terminal-background));              /* terminal bg */
  color: rgb(var(--v-theme-on-terminal-background)); /* Added for text contrast */
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
               "Liberation Mono", "Courier New", monospace;

  box-shadow: -2px 0 8px rgba(0,0,0,.4);
  // overflow-y: auto;
  // display: flex;
  // flex-direction: column;
  

  /* deep-override Vuetify’s v-list accent */
  ::v-deep(.v-list) {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;

    &::before,
    &::after { display: none !important; }
  }
}

/* ───── list & header colors (override Vuetify) ───── */
.slide-aside-panel {
  // HEADER
  ::v-deep(.log-header) {
    color: rgb(var(--v-theme-warning)) !important;
  }

  // LIST ITEM TITLES  (primary colour)
  ::v-deep(.v-list-item-title) {
    color: rgb(var(--v-theme-primary)) !important;
    font-weight: 500;
  }

  // LIST ITEM TEXT / SUBTITLES  (primary colour)
  ::v-deep(.v-list-item-content),
  ::v-deep(.v-list-item-subtitle),
  ::v-deep(.v-list-item__content > *:not(.v-list-item-title)) {
    color: rgb(var(--v-theme-secondary)) !important;
  }
}
</style>
